/**
 * Custom Mobility Theme — Main JavaScript
 * Navigation, mobile menu, scroll effects, FAQ accordion, form handling
 */

(function () {
  'use strict';

  /* ==========================================================================
     Header & Scroll Effects
     ========================================================================== */

  const header = document.querySelector('[data-header]');
  let lastScrollY = 0;
  const scrollThreshold = 50;

  function handleHeaderScroll() {
    const currentScrollY = window.scrollY;

    if (currentScrollY > scrollThreshold) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }

    lastScrollY = currentScrollY;
  }

  if (header) {
    window.addEventListener('scroll', handleHeaderScroll, { passive: true });
    handleHeaderScroll();
  }

  /* ==========================================================================
     Desktop Dropdown Navigation
     ========================================================================== */

  const dropdownItems = document.querySelectorAll('.has-dropdown');

  dropdownItems.forEach(function (item) {
    const button = item.querySelector('.header__menu-link');
    const dropdown = item.querySelector('.header__dropdown');

    if (!button || !dropdown) return;

    // Keyboard toggle
    button.addEventListener('click', function (e) {
      e.preventDefault();
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      closeAllDropdowns();
      if (!isOpen) {
        button.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('is-open');
      }
    });

    // Close on mouse leave (desktop hover handled by CSS, this is for keyboard)
    item.addEventListener('mouseleave', function () {
      button.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('is-open');
    });
  });

  function closeAllDropdowns() {
    dropdownItems.forEach(function (item) {
      var btn = item.querySelector('.header__menu-link');
      var dd = item.querySelector('.header__dropdown');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (dd) dd.classList.remove('is-open');
    });
  }

  // Close dropdowns on outside click
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.has-dropdown')) {
      closeAllDropdowns();
    }
  });

  // Close dropdowns on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeAllDropdowns();
      closeMobileMenu();
    }
  });

  /* ==========================================================================
     Mobile Menu
     ========================================================================== */

  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileMenuOverlay = document.querySelector('[data-mobile-menu-overlay]');

  function openMobileMenu() {
    if (!mobileMenu || !mobileMenuToggle) return;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    mobileMenuToggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mobile-menu-open');
    if (mobileMenuOverlay) mobileMenuOverlay.classList.add('is-visible');
  }

  function closeMobileMenu() {
    if (!mobileMenu || !mobileMenuToggle) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenuToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mobile-menu-open');
    if (mobileMenuOverlay) mobileMenuOverlay.classList.remove('is-visible');
  }

  if (mobileMenuToggle) {
    mobileMenuToggle.addEventListener('click', function () {
      var isOpen = mobileMenuToggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });
  }

  if (mobileMenuOverlay) {
    mobileMenuOverlay.addEventListener('click', closeMobileMenu);
  }

  /* ==========================================================================
     Mobile Menu Accordion
     ========================================================================== */

  const mobileAccordions = document.querySelectorAll('[data-mobile-accordion]');

  mobileAccordions.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var isExpanded = btn.getAttribute('aria-expanded') === 'true';
      var submenu = btn.nextElementSibling;

      // Close all others
      mobileAccordions.forEach(function (other) {
        if (other !== btn) {
          other.setAttribute('aria-expanded', 'false');
          var otherSub = other.nextElementSibling;
          if (otherSub) otherSub.setAttribute('aria-hidden', 'true');
        }
      });

      // Toggle current
      btn.setAttribute('aria-expanded', String(!isExpanded));
      if (submenu) {
        submenu.setAttribute('aria-hidden', String(isExpanded));
      }
    });
  });

  /* ==========================================================================
     Scroll Reveal Animation
     ========================================================================== */

  function initScrollReveal() {
    var revealElements = document.querySelectorAll('[data-scroll-reveal]');
    if (!revealElements.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -40px 0px',
        }
      );

      revealElements.forEach(function (el) {
        observer.observe(el);
      });
    } else {
      // Fallback: show all elements
      revealElements.forEach(function (el) {
        el.classList.add('is-revealed');
      });
    }
  }

  initScrollReveal();

  /* ==========================================================================
     Product Page: Thumbnail Gallery
     ========================================================================== */

  /* ==========================================================================
     Product Gallery: Arrows, Thumbnails, Zoom Lightbox, Touch Swipe
     ========================================================================== */

  var galleryData = document.querySelector('[data-product-images]');
  var productImages = [];
  var currentImageIndex = 0;

  if (galleryData) {
    try { productImages = JSON.parse(galleryData.textContent); } catch (e) {}
  }

  var mainImg = document.getElementById('product-main-img');
  var productThumbs = document.querySelectorAll('[data-product-thumb]');
  var zoomOverlay = document.querySelector('[data-zoom-overlay]');
  var zoomImg = document.querySelector('[data-zoom-img]');

  function setGalleryImage(index) {
    if (!productImages.length || !mainImg) return;
    if (index < 0) index = productImages.length - 1;
    if (index >= productImages.length) index = 0;
    currentImageIndex = index;

    mainImg.src = productImages[index].src;
    mainImg.setAttribute('data-zoom-src', productImages[index].zoom);

    productThumbs.forEach(function (t) { t.classList.remove('product-page__thumb--active'); });
    var activeThumb = document.querySelector('[data-thumb-index="' + index + '"]');
    if (activeThumb) activeThumb.classList.add('product-page__thumb--active');
  }

  // Thumbnail clicks and hover
  productThumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      var idx = parseInt(thumb.getAttribute('data-thumb-index'), 10);
      setGalleryImage(idx);
    });
    thumb.addEventListener('mouseenter', function () {
      var idx = parseInt(thumb.getAttribute('data-thumb-index'), 10);
      setGalleryImage(idx);
    });
  });

  // Arrow clicks
  var prevBtn = document.querySelector('[data-gallery-prev]');
  var nextBtn = document.querySelector('[data-gallery-next]');
  if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); setGalleryImage(currentImageIndex - 1); });
  if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); setGalleryImage(currentImageIndex + 1); });

  // Click to zoom
  var galleryWrap = document.querySelector('[data-product-gallery]');
  if (galleryWrap && zoomOverlay && zoomImg) {
    galleryWrap.addEventListener('click', function (e) {
      if (e.target.closest('.product-gallery__arrow')) return;
      var zoomSrc = mainImg.getAttribute('data-zoom-src');
      if (zoomSrc) {
        zoomImg.src = zoomSrc;
        zoomOverlay.classList.add('is-open');
        document.body.classList.add('mobile-menu-open');
      }
    });

    // Close zoom
    document.querySelector('[data-zoom-close]').addEventListener('click', function () {
      zoomOverlay.classList.remove('is-open');
      document.body.classList.remove('mobile-menu-open');
    });

    zoomOverlay.addEventListener('click', function (e) {
      if (e.target === zoomOverlay) {
        zoomOverlay.classList.remove('is-open');
        document.body.classList.remove('mobile-menu-open');
      }
    });

    // Zoom arrows
    document.querySelector('[data-zoom-prev]').addEventListener('click', function () {
      setGalleryImage(currentImageIndex - 1);
      zoomImg.src = productImages[currentImageIndex].zoom;
    });
    document.querySelector('[data-zoom-next]').addEventListener('click', function () {
      setGalleryImage(currentImageIndex + 1);
      zoomImg.src = productImages[currentImageIndex].zoom;
    });

    // Keyboard: Escape to close, arrows to navigate
    document.addEventListener('keydown', function (e) {
      if (!zoomOverlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        zoomOverlay.classList.remove('is-open');
        document.body.classList.remove('mobile-menu-open');
      }
      if (e.key === 'ArrowLeft') { setGalleryImage(currentImageIndex - 1); zoomImg.src = productImages[currentImageIndex].zoom; }
      if (e.key === 'ArrowRight') { setGalleryImage(currentImageIndex + 1); zoomImg.src = productImages[currentImageIndex].zoom; }
    });
  }

  // Touch swipe on main image
  if (galleryWrap && productImages.length > 1) {
    var touchStartX = 0;
    var touchEndX = 0;

    galleryWrap.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    galleryWrap.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) { setGalleryImage(currentImageIndex + 1); }
        else { setGalleryImage(currentImageIndex - 1); }
      }
    }, { passive: true });
  }

  /* ==========================================================================
     Product Page: Quantity Selector
     ========================================================================== */

  document.addEventListener('click', function (e) {
    if (e.target.matches('[data-quantity-minus]')) {
      var input = e.target.parentElement.querySelector('[data-quantity-input]');
      if (input) {
        var val = parseInt(input.value, 10);
        if (val > 1) {
          input.value = val - 1;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    if (e.target.matches('[data-quantity-plus]')) {
      var input = e.target.parentElement.querySelector('[data-quantity-input]');
      if (input) {
        var val = parseInt(input.value, 10);
        input.value = val + 1;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  /* ==========================================================================
     Cart Page: Quantity Buttons
     ========================================================================== */

  document.querySelectorAll('[data-cart-quantity-minus]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var key = btn.getAttribute('data-cart-quantity-minus');
      var input = document.querySelector('[data-cart-quantity-input="' + key + '"]');
      if (input) {
        var val = parseInt(input.value, 10);
        if (val > 0) {
          input.value = val - 1;
        }
      }
    });
  });

  document.querySelectorAll('[data-cart-quantity-plus]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var key = btn.getAttribute('data-cart-quantity-plus');
      var input = document.querySelector('[data-cart-quantity-input="' + key + '"]');
      if (input) {
        var val = parseInt(input.value, 10);
        input.value = val + 1;
      }
    });
  });

  /* ==========================================================================
     Product Variant Selection
     ========================================================================== */

  var variantSelect = document.querySelector('[data-product-variant-select]');
  var optionSelects = document.querySelectorAll('[data-product-option]');
  var priceWrapper = document.querySelector('[data-price-wrapper]');
  var quantityInput = document.querySelector('[data-quantity-input]');

  if (variantSelect && optionSelects.length) {
    optionSelects.forEach(function (select) {
      select.addEventListener('change', function () {
        updateVariantSelection();
      });
    });
  }

  if (quantityInput) {
    quantityInput.addEventListener('input', updatePriceDisplay);
    quantityInput.addEventListener('change', updatePriceDisplay);
  }

  // Format cents (integer) as money string using shop's money format
  function formatMoney(cents) {
    if (cents === null || cents === undefined || isNaN(cents)) return '';
    if (!priceWrapper) return '$' + (cents / 100).toFixed(2);
    var format = priceWrapper.getAttribute('data-money-format') || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var intWithCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formattedWithCents = intWithCommas + '.' + parts[1];
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, formattedWithCents)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, intWithCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formattedWithCents.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, intWithCommas);
  }

  function updatePriceDisplay() {
    if (!priceWrapper) return;

    var unitCents = parseInt(priceWrapper.getAttribute('data-unit-price-cents'), 10) || 0;
    var unitCompareCents = parseInt(priceWrapper.getAttribute('data-unit-compare-cents'), 10) || 0;
    var qty = 1;
    if (quantityInput) {
      qty = parseInt(quantityInput.value, 10) || 1;
      if (qty < 1) qty = 1;
    }

    var totalCents = unitCents * qty;
    var totalCompareCents = unitCompareCents * qty;

    var priceEl = priceWrapper.querySelector('[data-product-price]');
    var compareEl = priceWrapper.querySelector('[data-product-compare-price]');
    var freeShipEl = priceWrapper.querySelector('[data-free-ship]');

    if (priceEl) {
      if (unitCents === 0) {
        priceEl.textContent = 'Call for Pricing';
        priceEl.className = 'product-page__price-regular';
      } else if (unitCompareCents > unitCents) {
        priceEl.textContent = formatMoney(totalCents);
        priceEl.className = 'product-page__price-sale';
      } else {
        priceEl.textContent = formatMoney(totalCents);
        priceEl.className = 'product-page__price-regular';
      }
    }

    if (compareEl) {
      if (unitCompareCents > unitCents) {
        compareEl.textContent = formatMoney(totalCompareCents);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (freeShipEl) {
      freeShipEl.style.display = unitCents >= 50000 ? '' : 'none';
    }
  }

  function updateVariantSelection() {
    if (!variantSelect || !optionSelects.length) return;

    var selectedOptions = [];
    optionSelects.forEach(function (select) {
      selectedOptions.push(select.value);
    });

    var options = variantSelect.querySelectorAll('option');
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var matches = true;

      if (selectedOptions[0] && option.getAttribute('data-option1') !== selectedOptions[0]) {
        matches = false;
      }
      if (selectedOptions[1] && option.getAttribute('data-option2') !== selectedOptions[1]) {
        matches = false;
      }
      if (selectedOptions[2] && option.getAttribute('data-option3') !== selectedOptions[2]) {
        matches = false;
      }

      if (matches) {
        variantSelect.value = option.value;

        // Update price wrapper with new unit price, then refresh display
        if (priceWrapper) {
          var newUnitCents = option.getAttribute('data-variant-price-cents');
          var newCompareCents = option.getAttribute('data-variant-compare-cents');
          if (newUnitCents !== null) priceWrapper.setAttribute('data-unit-price-cents', newUnitCents);
          if (newCompareCents !== null) priceWrapper.setAttribute('data-unit-compare-cents', newCompareCents);
          updatePriceDisplay();
        }

        // Update add to cart button
        var addToCartBtn = document.querySelector('.product-page__add-to-cart');
        if (addToCartBtn) {
          if (option.disabled) {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = 'Sold Out';
          } else {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = 'Add to Cart';
          }
        }

        // Update product image to variant image
        var variantImage = option.getAttribute('data-variant-image');
        var variantImageZoom = option.getAttribute('data-variant-image-zoom');
        var variantImageAlt = option.getAttribute('data-variant-image-alt');
        if (variantImage) {
          var mainImg = document.getElementById('product-main-img');
          if (mainImg) {
            mainImg.src = variantImage;
            mainImg.setAttribute('data-zoom-src', variantImageZoom || variantImage);
            if (variantImageAlt) mainImg.alt = variantImageAlt;
          }

          // Highlight matching thumbnail
          var thumbs = document.querySelectorAll('[data-product-thumb]');
          thumbs.forEach(function (thumb) {
            thumb.classList.remove('product-page__thumb--active');
            if (thumb.getAttribute('data-image-url') === variantImage) {
              thumb.classList.add('product-page__thumb--active');
            }
          });
        }

        break;
      }
    }
  }

  /* ==========================================================================
     Product Page Tabs
     ========================================================================== */

  document.querySelectorAll('[data-tab-btn]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var tabName = btn.getAttribute('data-tab-btn');
      var tabsContainer = btn.closest('.product-page__tabs');
      if (!tabsContainer) return;

      // Deactivate all buttons and panels
      tabsContainer.querySelectorAll('[data-tab-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      tabsContainer.querySelectorAll('[data-tab-panel]').forEach(function (p) { p.classList.remove('is-active'); });

      // Activate clicked
      btn.classList.add('is-active');
      var panel = tabsContainer.querySelector('[data-tab-panel="' + tabName + '"]');
      if (panel) panel.classList.add('is-active');
    });
  });

  /* ==========================================================================
     Hero Lead Gen Form — Type Toggle (Individual / Organization)
     ========================================================================== */

  var formTypeBtns = document.querySelectorAll('[data-form-type]');
  var orgField = document.querySelector('.hero-form__field--org');
  var formTypeHidden = document.querySelector('[data-form-type-value]');
  var orgCards = document.querySelectorAll('.hero-form__interest-card--org');

  formTypeBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var type = btn.getAttribute('data-form-type');

      // Toggle active state
      formTypeBtns.forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');

      // Show/hide organization name field
      if (orgField) {
        orgField.style.display = type === 'organization' ? '' : 'none';
      }

      // Show/hide organization-only interest cards
      orgCards.forEach(function (card) {
        if (type === 'organization') {
          card.style.display = '';
        } else {
          card.style.display = 'none';
          // Uncheck org cards when switching back
          var cb = card.querySelector('input[type="checkbox"]');
          if (cb) cb.checked = false;
        }
      });

      // Update hidden value
      if (formTypeHidden) {
        formTypeHidden.value = type === 'organization' ? 'Organization' : 'Individual';
      }
    });
  });

  /* ==========================================================================
     FAQ Accordion (for FAQ pages with [data-faq-accordion])
     ========================================================================== */

  document.querySelectorAll('[data-faq-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var isExpanded = btn.getAttribute('aria-expanded') === 'true';
      var content = btn.nextElementSibling;

      btn.setAttribute('aria-expanded', String(!isExpanded));
      if (content) {
        content.setAttribute('aria-hidden', String(isExpanded));
        if (isExpanded) {
          content.style.maxHeight = '0';
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      }
    });
  });

  /* ==========================================================================
     Collection Page: Sort, Filter Toggle, Filter Drawer
     ========================================================================== */

  // Sort dropdown
  var sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  // Filter drawer toggle (mobile)
  var filterToggle = document.querySelector('[data-filter-toggle]');
  var filterSidebar = document.querySelector('[data-filter-sidebar]');
  var filterClose = document.querySelector('[data-filter-close]');
  var filterOverlay = document.querySelector('[data-filter-overlay]');

  function openFilterDrawer() {
    if (!filterSidebar) return;
    filterSidebar.classList.add('is-open');
    filterSidebar.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mobile-menu-open');
    if (filterOverlay) filterOverlay.classList.add('is-visible');
  }

  function closeFilterDrawer() {
    if (!filterSidebar) return;
    filterSidebar.classList.remove('is-open');
    filterSidebar.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mobile-menu-open');
    if (filterOverlay) filterOverlay.classList.remove('is-visible');
  }

  if (filterToggle) {
    filterToggle.addEventListener('click', openFilterDrawer);
  }
  if (filterClose) {
    filterClose.addEventListener('click', closeFilterDrawer);
  }
  if (filterOverlay) {
    filterOverlay.addEventListener('click', closeFilterDrawer);
  }

  // Filter group accordion
  document.querySelectorAll('[data-filter-group-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var isExpanded = btn.getAttribute('aria-expanded') === 'true';
      var content = btn.nextElementSibling;
      btn.setAttribute('aria-expanded', String(!isExpanded));
      if (content) {
        content.setAttribute('aria-hidden', String(isExpanded));
      }
    });
  });

  // Filter form submission — build URL from checked filters + price range
  var filterForm = document.querySelector('[data-filter-form]');
  if (filterForm) {
    filterForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var url = new URL(window.location.origin + window.location.pathname);
      // Preserve sort
      var currentUrl = new URL(window.location.href);
      var sortBy = currentUrl.searchParams.get('sort_by');
      if (sortBy) url.searchParams.set('sort_by', sortBy);

      // Collect checked values
      filterForm.querySelectorAll('input[type="checkbox"]:checked').forEach(function (cb) {
        url.searchParams.append(cb.name, cb.value);
      });

      // Collect price range
      filterForm.querySelectorAll('input[type="number"]').forEach(function (input) {
        if (input.value && input.value !== '') {
          url.searchParams.set(input.name, input.value);
        }
      });

      window.location.href = url.toString();
    });
  }

  /* ==========================================================================
     Smooth Anchor Links
     ========================================================================== */

  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener('click', function (e) {
      var targetId = this.getAttribute('href');
      if (targetId === '#') return;

      var target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        var headerOffset = header ? header.offsetHeight : 0;
        var elementPosition = target.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.scrollY - headerOffset - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    });
  });

  /* ==========================================================================
     Form Validation Enhancement
     ========================================================================== */

  document.querySelectorAll('form').forEach(function (form) {
    form.addEventListener('submit', function () {
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.classList.add('is-loading');
      }
    });
  });

})();
